import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  X,
  TrendingUp,
  LineChart,
  Table,
  History,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import PriceTrendChart from './PriceTrendChart';
import PriceHistoryTable from './PriceHistoryTable';
import productService from '../services/productService';

const PriceHistoryModal = ({
  open,
  onClose,
  // Search criteria - new approach
  searchCriteria = null,
  // Optional: pre-fill values for search fields
  initialTipDiameter = '',
  initialTotalLength = '',
  initialCategory = '',
  initialClientName = '',
  initialProductName = '',
  onPriceSelect,
  mode = 'quotation' // quotation | review | standalone
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmall = useMediaQuery('(max-width: 640px)');

  // Component state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priceHistory, setPriceHistory] = useState([]);
  const [currentTab, setCurrentTab] = useState('table');
  const [searchExpanded, setSearchExpanded] = useState(true);

  // Search form state
  const [searchForm, setSearchForm] = useState({
    tipDiameterMode: 'range',
    tipDiameter: initialTipDiameter,
    tipDiameterMin: '',
    tipDiameterMax: '',
    totalLengthMode: 'range',
    totalLength: initialTotalLength,
    totalLengthMin: '',
    totalLengthMax: '',
    category: initialCategory,
    clientName: initialClientName,
    productName: initialProductName
  });

  // Statistics with enhanced tax pricing
  const [stats, setStats] = useState({
    totalRecords: 0,
    avgPrice: 0,
    minPrice: 0,
    maxPrice: 0,
    quotationCount: 0,
    salesOrderCount: 0,
    uniqueClients: 0,
    uniqueProducts: 0,
    taxInclusiveCount: 0,
    taxExclusiveCount: 0,
    avgTaxInclusivePrice: 0,
    avgTaxExclusivePrice: 0
  });

  // Update search form when initial values change
  useEffect(() => {
    if (open) {
      setSearchForm(prev => ({
        ...prev,
        tipDiameter: initialTipDiameter || prev.tipDiameter,
        totalLength: initialTotalLength || prev.totalLength,
        category: initialCategory || prev.category,
        clientName: initialClientName || prev.clientName,
        productName: initialProductName || prev.productName
      }));
    }
  }, [open, initialTipDiameter, initialTotalLength, initialCategory, initialClientName, initialProductName]);

  // Build search criteria from form
  const buildSearchCriteria = useCallback(() => {
    const criteria = {
      page: 1,
      pageSize: 50,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    // Tip diameter
    if (searchForm.tipDiameterMode === 'exact' && searchForm.tipDiameter) {
      criteria.tipDiameter = parseFloat(searchForm.tipDiameter);
    } else if (searchForm.tipDiameterMode === 'range') {
      if (searchForm.tipDiameterMin) criteria.tipDiameterMin = parseFloat(searchForm.tipDiameterMin);
      if (searchForm.tipDiameterMax) criteria.tipDiameterMax = parseFloat(searchForm.tipDiameterMax);
    }

    // Total length
    if (searchForm.totalLengthMode === 'exact' && searchForm.totalLength) {
      criteria.totalLength = parseFloat(searchForm.totalLength);
    } else if (searchForm.totalLengthMode === 'range') {
      if (searchForm.totalLengthMin) criteria.totalLengthMin = parseFloat(searchForm.totalLengthMin);
      if (searchForm.totalLengthMax) criteria.totalLengthMax = parseFloat(searchForm.totalLengthMax);
    }

    // Text filters
    if (searchForm.category) criteria.category = searchForm.category;
    if (searchForm.clientName) criteria.clientName = searchForm.clientName;
    if (searchForm.productName) criteria.productName = searchForm.productName;

    return criteria;
  }, [searchForm]);

  // Load price history
  const loadPriceHistory = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Use provided searchCriteria or build from form
      const criteria = searchCriteria || buildSearchCriteria();
      const response = await productService.searchAdvancedPriceHistory(criteria);

      // Handle response
      const history = response.price_history || [];
      const historyArray = Array.isArray(history) ? history : [];

      // Process and normalize data
      const processedHistory = historyArray.map((item, index) => ({
        ...item,
        price: parseFloat(item.price || item.unit_price || item.base_price || 0),
        quantity: parseInt(item.quantity || item.qty || 1),
        date: item.created_at || item.date || item.timestamp || new Date().toISOString(),
        client_name: item.client_name || item.customer_name || item.client || item.customer || '未知客户',
        source_type: item.source_type || item.type || 'quotation',
        reference_id: item.quotation_id || item.sales_order_id || item.id || `${Date.now()}-${index}`,
        base_price: parseFloat(item.base_price || item.price || item.unit_price || 0),
        pricing_mode: item.pricing_mode || 'tax_exclusive',
        tax_rate: parseFloat(item.tax_rate || 0.13),
        tax_exclusive_price: parseFloat(item.tax_exclusive_price || item.price || item.unit_price || 0),
        tax_inclusive_price: parseFloat(item.tax_inclusive_price || (item.price || item.unit_price || 0) * 1.13),
        tax_amount: parseFloat(item.tax_amount || (item.price || item.unit_price || 0) * 0.13),
        formatted_tax_rate: item.formatted_tax_rate || '13.0%',
        // Include product_id for display
        product_id: item.product_id || ''
      })).filter(item => item.price > 0);

      setPriceHistory(processedHistory);

      // Use statistics from response or calculate
      if (response.statistics) {
        setStats({
          totalRecords: response.statistics.total_records || processedHistory.length,
          avgPrice: response.statistics.avg_price || 0,
          minPrice: response.statistics.min_price || 0,
          maxPrice: response.statistics.max_price || 0,
          quotationCount: response.statistics.quotation_count || 0,
          salesOrderCount: response.statistics.sales_order_count || 0,
          uniqueClients: response.statistics.unique_clients || 0,
          uniqueProducts: response.statistics.unique_products || 0,
          taxInclusiveCount: response.statistics.tax_inclusive_count || 0,
          taxExclusiveCount: response.statistics.tax_exclusive_count || 0,
          avgTaxInclusivePrice: response.statistics.avg_tax_inclusive_price || 0,
          avgTaxExclusivePrice: response.statistics.avg_tax_exclusive_price || 0
        });
      } else if (processedHistory.length > 0) {
        // Calculate statistics locally
        const prices = processedHistory.map(item => item.price);
        const clients = [...new Set(processedHistory.map(item => item.client_name))];
        const products = [...new Set(processedHistory.map(item => item.product_id))];

        setStats({
          totalRecords: processedHistory.length,
          avgPrice: prices.reduce((sum, price) => sum + price, 0) / prices.length,
          minPrice: Math.min(...prices),
          maxPrice: Math.max(...prices),
          quotationCount: processedHistory.filter(item => item.source_type === 'quotation').length,
          salesOrderCount: processedHistory.filter(item => item.source_type === 'sales_order').length,
          uniqueClients: clients.length,
          uniqueProducts: products.length,
          taxInclusiveCount: 0,
          taxExclusiveCount: 0,
          avgTaxInclusivePrice: 0,
          avgTaxExclusivePrice: 0
        });
      } else {
        // Reset stats
        setStats({
          totalRecords: 0, avgPrice: 0, minPrice: 0, maxPrice: 0,
          quotationCount: 0, salesOrderCount: 0, uniqueClients: 0, uniqueProducts: 0,
          taxInclusiveCount: 0, taxExclusiveCount: 0,
          avgTaxInclusivePrice: 0, avgTaxExclusivePrice: 0
        });
      }

      // Collapse search after successful search
      if (processedHistory.length > 0) {
        setSearchExpanded(false);
      }

    } catch (err) {
      console.error('Price history loading error:', err);
      setError(err.response?.data?.error || err.message || '加载价格历史失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [searchCriteria, buildSearchCriteria]);

  // Auto-search if searchCriteria is provided or initial values exist
  useEffect(() => {
    if (open) {
      // Reset tab and expand search
      setCurrentTab('table');

      // Auto-search if we have criteria
      if (searchCriteria ||
          initialTipDiameter || initialTotalLength || initialCategory ||
          initialClientName || initialProductName) {
        loadPriceHistory();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle search form change
  const handleFormChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle search
  const handleSearch = () => {
    loadPriceHistory();
  };

  // Handle clear
  const handleClear = () => {
    setSearchForm({
      tipDiameterMode: 'range',
      tipDiameter: '',
      tipDiameterMin: '',
      tipDiameterMax: '',
      totalLengthMode: 'range',
      totalLength: '',
      totalLengthMin: '',
      totalLengthMax: '',
      category: '',
      clientName: '',
      productName: ''
    });
    setPriceHistory([]);
    setStats({
      totalRecords: 0, avgPrice: 0, minPrice: 0, maxPrice: 0,
      quotationCount: 0, salesOrderCount: 0, uniqueClients: 0, uniqueProducts: 0,
      taxInclusiveCount: 0, taxExclusiveCount: 0,
      avgTaxInclusivePrice: 0, avgTaxExclusivePrice: 0
    });
    setSearchExpanded(true);
  };

  // Handle price selection
  const handlePriceSelect = (priceData) => {
    if (onPriceSelect && typeof onPriceSelect === 'function') {
      onPriceSelect(priceData);
    }

    if (mode === 'quotation' || mode === 'sales') {
      onClose();
    }
  };

  // Get search summary for header
  const getSearchSummary = () => {
    const parts = [];
    if (searchForm.tipDiameter || searchForm.tipDiameterMin || searchForm.tipDiameterMax) {
      parts.push(`刀尖直径`);
    }
    if (searchForm.totalLength || searchForm.totalLengthMin || searchForm.totalLengthMax) {
      parts.push(`总长度`);
    }
    if (searchForm.category) parts.push(`分类: ${searchForm.category}`);
    if (searchForm.clientName) parts.push(`客户: ${searchForm.clientName}`);
    if (searchForm.productName) parts.push(`产品: ${searchForm.productName}`);
    return parts.length > 0 ? parts.join(' | ') : '请输入搜索条件';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${isSmall ? 'max-w-full h-full' : 'max-w-5xl max-h-[90vh]'} overflow-hidden flex flex-col`}>
        {/* Dialog Header */}
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              <div>
                <DialogTitle>价格历史搜索</DialogTitle>
                <p className="text-sm text-muted-foreground">{getSearchSummary()}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <Separator />

        {/* Dialog Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Collapsible Search Form */}
          <Collapsible open={searchExpanded} onOpenChange={setSearchExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full flex items-center justify-between p-3 hover:bg-muted">
                <span className="text-sm font-medium">搜索条件</span>
                {searchExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-4 bg-muted/30 border-b">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Tip Diameter */}
                  <div className="space-y-1">
                    <Label className="text-xs">刀尖直径 (mm)</Label>
                    <div className="flex items-center gap-1">
                      <Select
                        value={searchForm.tipDiameterMode}
                        onValueChange={(v) => handleFormChange('tipDiameterMode', v)}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exact">精确</SelectItem>
                          <SelectItem value="range">范围</SelectItem>
                        </SelectContent>
                      </Select>
                      {searchForm.tipDiameterMode === 'exact' ? (
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="值"
                          className="h-8 text-sm"
                          value={searchForm.tipDiameter}
                          onChange={(e) => handleFormChange('tipDiameter', e.target.value)}
                        />
                      ) : (
                        <>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="最小"
                            className="h-8 text-sm"
                            value={searchForm.tipDiameterMin}
                            onChange={(e) => handleFormChange('tipDiameterMin', e.target.value)}
                          />
                          <span className="text-xs">-</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="最大"
                            className="h-8 text-sm"
                            value={searchForm.tipDiameterMax}
                            onChange={(e) => handleFormChange('tipDiameterMax', e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Total Length */}
                  <div className="space-y-1">
                    <Label className="text-xs">总长度 (mm)</Label>
                    <div className="flex items-center gap-1">
                      <Select
                        value={searchForm.totalLengthMode}
                        onValueChange={(v) => handleFormChange('totalLengthMode', v)}
                      >
                        <SelectTrigger className="w-16 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exact">精确</SelectItem>
                          <SelectItem value="range">范围</SelectItem>
                        </SelectContent>
                      </Select>
                      {searchForm.totalLengthMode === 'exact' ? (
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="值"
                          className="h-8 text-sm"
                          value={searchForm.totalLength}
                          onChange={(e) => handleFormChange('totalLength', e.target.value)}
                        />
                      ) : (
                        <>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="最小"
                            className="h-8 text-sm"
                            value={searchForm.totalLengthMin}
                            onChange={(e) => handleFormChange('totalLengthMin', e.target.value)}
                          />
                          <span className="text-xs">-</span>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="最大"
                            className="h-8 text-sm"
                            value={searchForm.totalLengthMax}
                            onChange={(e) => handleFormChange('totalLengthMax', e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Category */}
                  <div className="space-y-1">
                    <Label className="text-xs">分类</Label>
                    <Input
                      placeholder="产品分类"
                      className="h-8 text-sm"
                      value={searchForm.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                    />
                  </div>

                  {/* Client Name */}
                  <div className="space-y-1">
                    <Label className="text-xs">客户名称</Label>
                    <Input
                      placeholder="客户名称"
                      className="h-8 text-sm"
                      value={searchForm.clientName}
                      onChange={(e) => handleFormChange('clientName', e.target.value)}
                    />
                  </div>

                  {/* Product Name */}
                  <div className="space-y-1">
                    <Label className="text-xs">产品名称</Label>
                    <Input
                      placeholder="产品名称"
                      className="h-8 text-sm"
                      value={searchForm.productName}
                      onChange={(e) => handleFormChange('productName', e.target.value)}
                    />
                  </div>
                </div>

                {/* Search Buttons */}
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" onClick={handleSearch} disabled={loading}>
                    {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                    搜索
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleClear}>
                    <X className="h-3 w-3 mr-1" />
                    清空
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Results Area */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">正在搜索价格历史...</p>
              </div>
            ) : error ? (
              <div className="p-6">
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between">
                    {error}
                    <Button size="sm" variant="outline" onClick={loadPriceHistory}>
                      重试
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : priceHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <History className="h-16 w-16 text-muted-foreground" />
                <h3 className="text-lg font-medium text-muted-foreground">暂无价格历史</h3>
                <p className="text-sm text-muted-foreground">请输入搜索条件查询历史价格</p>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                {/* Statistics Summary */}
                <div className="p-3 bg-muted border-b">
                  <div className="flex flex-wrap gap-2 items-center">
                    <Badge variant="outline" className="gap-1 text-xs">
                      <History className="h-3 w-3" />
                      {stats.totalRecords} 条记录
                    </Badge>
                    <Badge variant="outline" className="text-primary border-primary/30 text-xs">
                      ¥{stats.avgPrice.toFixed(2)} 平均价格
                    </Badge>
                    <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                      {stats.quotationCount} 报价单
                    </Badge>
                    <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                      {stats.salesOrderCount} 销售订单
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {stats.uniqueClients} 个客户
                    </Badge>
                    {stats.uniqueProducts > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {stats.uniqueProducts} 个产品
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <Tabs value={currentTab} onValueChange={setCurrentTab} className="flex-1 flex flex-col">
                  <div className="px-4 border-b">
                    <TabsList className="h-10">
                      <TabsTrigger value="table" className="gap-1 text-sm">
                        <Table className="h-3 w-3" />
                        {isMobile ? '明细' : '详细记录'}
                      </TabsTrigger>
                      <TabsTrigger value="chart" className="gap-1 text-sm">
                        <LineChart className="h-3 w-3" />
                        {isMobile ? '趋势' : '价格趋势'}
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-auto p-4">
                    <TabsContent value="table" className="mt-0">
                      <PriceHistoryTable
                        data={priceHistory}
                        onApplyPrice={handlePriceSelect}
                        pageSize={isMobile ? 5 : 10}
                        showFilters={true}
                        showActions={mode === 'quotation' || mode === 'sales'}
                        sortable={true}
                        showProductIdColumn={true}
                      />
                    </TabsContent>

                    <TabsContent value="chart" className="mt-0">
                      <div>
                        <PriceTrendChart
                          data={priceHistory}
                          height={isMobile ? 250 : 300}
                          showTrendLine={true}
                          showAverage={true}
                          interactive={true}
                        />

                        {priceHistory.length >= 2 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-semibold mb-2">最新价格动态</h4>
                            <div className="flex flex-wrap gap-2">
                              {priceHistory
                                .slice()
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .slice(0, 3)
                                .map((item, index) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className={item.source_type === 'quotation' ? 'border-blue-300 text-blue-600' : 'border-green-300 text-green-600'}
                                  >
                                    {item.client_name}: ¥{item.price.toFixed(2)}
                                  </Badge>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            )}
          </div>
        </div>

        {/* Dialog Actions */}
        <Separator />
        <DialogFooter className="p-4">
          <Button variant="outline" onClick={onClose}>
            关闭
          </Button>

          {(mode === 'quotation' || mode === 'sales') && priceHistory.length > 0 && (
            <Button onClick={loadPriceHistory} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TrendingUp className="h-4 w-4 mr-2" />
              )}
              刷新数据
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PriceHistoryModal;
